import React, { use, useCallback, useMemo } from 'react';
import {
  type EntitiesContext,
  type EntitiesContextProps,
  type EntityItem,
  type EntityRequestItem,
} from './types';
import useSyncPersistedEntities from './useSyncPersistedEntities';

export type { EntitiesContext, EntityItem, EntityRequestItem } from './types';

export const PersistedEntitiesContext = React.createContext<EntitiesContext | null>(null);

const PersistedEntitiesProvider: React.FC<EntitiesContextProps> = ({ children }) => {
  const { entities, persistEntity, loading, error } = useSyncPersistedEntities();

  const getEntity = useCallback(
    <T = unknown,>(key: string) => {
      if (!entities?.[key]) {
        return undefined;
      }

      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      return entities[key] as EntityItem<T>[];
    },
    [entities],
  );

  const setEntity = useCallback(
    (key: string, value: unknown, id?: string) => {
      const item: EntityRequestItem = {
        entityKey: key,
        entityValue: value,
      };

      if (id) {
        item.id = id;
      }

      return persistEntity(item);
    },
    [persistEntity],
  );

  const memoValue = useMemo(
    () => ({
      getEntity,
      setEntity,
      loading,
      entities,
      error: !!error,
    }),
    [getEntity, setEntity, loading, entities, error],
  );

  return <PersistedEntitiesContext value={memoValue}>{children}</PersistedEntitiesContext>;
};

export default PersistedEntitiesProvider;

export const usePersistedEntitiesContext = () => {
  const context = use(PersistedEntitiesContext);

  if (!context) {
    throw new Error('usePersistedEntitiesContext must be used within a PersistedEntitiesProvider');
  }

  return context;
};
